import { memo, useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getCheapestPackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { createOfferStructuredData, SCHEMA_TYPES } from 'components/utils/OfferStructuredData'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import useBedbankMetasearchDiscountBasePrice from 'hooks/useBedbankMetasearchDiscountBasePrice'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useGhaVerifierStructuredPrice } from 'hooks/useGhaVerifierPrice'

interface Props {
  offer: App.BedbankOffer
}

function HotelStructuredData(props: Props) {
  const { offer } = props
  const utmBp = useAppSelector(state => state.utm.bp)
  const geo = useContext(GeoContext)
  const contextCurrency = useContext(CurrencyContext)
  const { checkIn, checkOut } = useAppSelector((state) => state.router.location.query)
  const offerPageState = useContext(OfferPageStateContext)

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const cheapestPackageSelectionsByRoom = useAppSelector(appState => getCheapestPackagesByRoom(offerPageState, appState))

  // Look up the discount already fetched by `BedbankOfferContainer` for the cheapest hotel-only rate.
  // The request key includes the price, so both sides derive it from the same hook.
  const { checkInDate, checkOutDate, rooms } = offerPageState
  const { discountBasePrice } = useBedbankMetasearchDiscountBasePrice(offer, rooms, checkInDate, checkOutDate)
  const [discountAmount] = useMetasearchDiscount(discountBasePrice ?? 0, {
    propertyId: offer.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms,
    // Only metasearch traffic emits makesOffer, so skip the lookup entirely for organic visitors.
    disabled: utmBp !== '3',
  })

  const availablePackageRates = useMemo(() =>
    packageViews.flatMap(packageView => {
      if (fetchingRates || !packageView.show || !packageView.available) {
        return
      }
      return packageView.rates.map(rate => {
        if (rate.isFlightBundle) {
          return
        }
        return rate.totals
      })
    }).filter(Boolean),
  [fetchingRates, packageViews])

  const currency = contextCurrency ?? geo.currentCurrency

  const ghaVerifierPrice = useGhaVerifierStructuredPrice()

  const pricingData = useMemo(() => {
    if (utmBp !== '3' || !checkIn || !checkOut) {
      return {}
    }

    // if utm_bp=3, Get the hotelOnly key and extract the rate object
    const hotelOnly = cheapestPackageSelectionsByRoom?.hotelOnly
    if (!hotelOnly || typeof hotelOnly !== 'object') {
      return {}
    }

    const hotelOnlyKeys = Object.keys(hotelOnly)
    const firstKey = hotelOnlyKeys[0]
    if (!firstKey) {
      return {}
    }

    const firstSelection = hotelOnly[firstKey]
    const totals = firstSelection?.rate?.totals

    if (!totals?.total || totals.total === 0) {
      return {}
    }

    const { propertyFees, total = 0, exclusive, taxesAndFees } = totals

    const allOffers = availablePackageRates?.length > 0 ? availablePackageRates.map(total => createOfferStructuredData(checkIn, checkOut, {
      propertyFees: total?.propertyFees,
      offerPrice: total?.total || 0,
      offerPriceBeforeTax: total?.exclusive,
      taxesAndFees: total?.taxesAndFees,
    }, currency)) : []

    return {
      // Discount the cheapest/prominent offer only (the price GHA verifies), or publish Google's
      // own figures to the verifier so the markup and the rendered price agree.
      makesOffer: [createOfferStructuredData(checkIn, checkOut, ghaVerifierPrice ?? {
        propertyFees,
        offerPrice: Math.max(total - discountAmount, 0),
        offerPriceBeforeTax: exclusive,
        taxesAndFees,
      }, currency),
      ...allOffers,
      ],
    }
  }, [utmBp, checkIn, checkOut, cheapestPackageSelectionsByRoom, currency, availablePackageRates, discountAmount, ghaVerifierPrice])

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': SCHEMA_TYPES.HOTEL,
    name: offer.name,
    description: offer.metaDescription,
    identifier: offer.id,
    address: {
      '@type': SCHEMA_TYPES.POSTAL_ADDRESS,
      addressCountry: offer.property.address.countryCode,
      addressRegion: offer.property.address.stateProvinceName ?? offer.property.address.countryName,
      addressLocality: offer.property.address.city,
      postalCode: offer.property.address.postalCode,
      streetAddress: offer.property.address.line1 + (offer.property.address.line2 ? ' ' + offer.property.address.line2 : ''),
    },
    ...pricingData,
    telephone: offer.property.phone,
  }), [offer, pricingData])

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default memo(HotelStructuredData)
